/**
 * Public-facing multi-step navigation for campaigns and tools.
 * RTL and mobile accessible. Renders a step indicator (pills / dots / bar)
 * and back/next controls — each part optional so the host page can place
 * them independently (progress on top, buttons under the content, etc.)
 * per the document's pageSettings.
 */
import React from 'react';
import type { MultiPageDef, PageSettings } from '../../types';
import { sortedPages, resolvePageSettings } from './multiPageUtils';

interface MultiStepNavigationProps {
  pages: MultiPageDef[];
  currentPageId: string;
  onNavigate: (pageId: string) => void;
  /** If true, the next button is disabled (e.g. validation failed) */
  canAdvance: boolean;
  /** Label for the final-page button (e.g. "תרום" or "שלח") */
  finalLabel?: string;
  /** Called when user clicks next on the final page */
  onFinalSubmit?: () => void;
  primaryColor?: string;
  /** Which parts to render — defaults keep the legacy combined layout */
  showProgress?: boolean;
  showButtons?: boolean;
  /** Visual style of the step indicator (from pageSettings.progressStyle) */
  progressStyle?: PageSettings['progressStyle'];
}

const MultiStepNavigation: React.FC<MultiStepNavigationProps> = ({
  pages,
  currentPageId,
  onNavigate,
  canAdvance,
  finalLabel = 'שלח',
  onFinalSubmit,
  primaryColor = '#2563eb',
  showProgress = true,
  showButtons = true,
  progressStyle,
}) => {
  const sorted = sortedPages(pages);
  const currentIdx = sorted.findIndex(p => p.id === currentPageId);
  const isFirst = currentIdx === 0;
  const isLast = currentIdx === sorted.length - 1;
  const style = progressStyle || resolvePageSettings(null).progressStyle;

  const goNext = () => {
    if (isLast) {
      onFinalSubmit?.();
    } else if (canAdvance) {
      onNavigate(sorted[currentIdx + 1].id);
    }
  };

  const goBack = () => {
    if (!isFirst) onNavigate(sorted[currentIdx - 1].id);
  };

  const progressBarProps = {
    role: 'progressbar' as const,
    'aria-valuenow': currentIdx + 1,
    'aria-valuemin': 1,
    'aria-valuemax': sorted.length,
  };

  const renderPills = () => (
    <div className="flex items-center justify-center gap-1 px-4" {...progressBarProps}>
      {sorted.map((page, idx) => {
        const isActive = idx === currentIdx;
        const isDone = idx < currentIdx;
        return (
          <React.Fragment key={page.id}>
            {idx > 0 && (
              <div
                className="flex-1 h-0.5 max-w-[60px] rounded-full transition-colors duration-300"
                style={{ backgroundColor: isDone ? primaryColor : '#e5e7eb' }}
              />
            )}
            <button
              onClick={() => {
                if (idx < currentIdx) onNavigate(page.id);
              }}
              disabled={idx > currentIdx}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${
                isActive
                  ? 'text-white shadow-md'
                  : isDone
                  ? 'text-white opacity-70 hover:opacity-100 cursor-pointer'
                  : 'text-gray-400 bg-gray-100 cursor-default'
              }`}
              style={isActive || isDone ? { backgroundColor: primaryColor } : {}}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`${page.name} - שלב ${idx + 1} מתוך ${sorted.length}`}
            >
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] border-2 border-white/30">
                {isDone ? '✓' : idx + 1}
              </span>
              <span className="hidden sm:inline">{page.name}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderDots = () => (
    <div className="flex flex-col items-center gap-1.5 px-4" {...progressBarProps}>
      <div className="flex items-center justify-center gap-2">
        {sorted.map((page, idx) => {
          const isActive = idx === currentIdx;
          const isDone = idx < currentIdx;
          return (
            <button
              key={page.id}
              onClick={() => { if (isDone) onNavigate(page.id); }}
              disabled={idx > currentIdx}
              className="rounded-full transition-all"
              style={{
                width: isActive ? 22 : 8,
                height: 8,
                backgroundColor: isActive || isDone ? primaryColor : '#e5e7eb',
                opacity: isDone ? 0.6 : 1,
                cursor: isDone ? 'pointer' : 'default',
                border: 'none',
                padding: 0,
              }}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`${page.name} - שלב ${idx + 1} מתוך ${sorted.length}`}
            />
          );
        })}
      </div>
      <span className="text-[11px] font-bold text-gray-500">{sorted[currentIdx]?.name}</span>
    </div>
  );

  const renderBar = () => {
    const pct = sorted.length > 1 ? (currentIdx / (sorted.length - 1)) * 100 : 100;
    return (
      <div className="px-4" {...progressBarProps}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-bold text-gray-600">{sorted[currentIdx]?.name}</span>
          <span className="text-[11px] text-gray-400">שלב {currentIdx + 1} מתוך {sorted.length}</span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: primaryColor }}
          />
        </div>
      </div>
    );
  };

  return (
    <div dir="rtl" className="w-full">
      {showProgress && (
        <div className={showButtons ? 'mb-4' : ''}>
          {style === 'dots' ? renderDots() : style === 'bar' ? renderBar() : renderPills()}
        </div>
      )}

      {showButtons && (
        <div className="flex items-center justify-between gap-3 px-4">
          <button
            onClick={goBack}
            disabled={isFirst}
            className={`flex items-center gap-1 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              isFirst
                ? 'opacity-0 pointer-events-none'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95'
            }`}
            aria-label="חזור לעמוד הקודם"
          >
            <svg className="w-4 h-4 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            הקודם
          </button>

          <button
            onClick={goNext}
            disabled={!canAdvance && !isLast}
            className="flex items-center gap-1 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            style={{ backgroundColor: primaryColor }}
            aria-label={isLast ? finalLabel : 'המשך לעמוד הבא'}
          >
            {isLast ? finalLabel : 'הבא'}
            {!isLast && (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MultiStepNavigation;
